import React, { Fragment } from "react";
import { Link } from "react-router-dom";

import { Helmet } from "react-helmet";

import NavbarB from "../components/navbar-b";
import FooterB from "../components/footer-b";
import "./editorial.css";

const Editorial = (props) => {
  return (
    <div className="editorial-container">
      <Helmet>
        <title>Editorial - FF Season 2026</title>
        <meta property="og:title" content="Editorial - FF Season 2026" />
        <link rel="canonical" href="https://feelers-feelers.com/editorial" />
      </Helmet>
      <div className="editorial-bg-container">
        <img
          alt="image"
          src="/machineyearning/MY-main-bg.jpg"
          className="editorial-image-bg"
        />
      </div>
      <div className="editorial-navbar-container">
        <NavbarB rootClassName="navbar-broot-class-name5"></NavbarB>
      </div>
      <div className="editorial-body">
        <Link to="/" className="editorial-body-back MY-Subtitle">
          &lt; BACK
        </Link>
        <div className="editorial-body-content MY-Body">
          <span className="editorial-text-title MY-Headline">
            <b>LIFE IN THE WAKE</b>
          </span>
          A worldview produces technology. A technology in return produces a
          worldview. A recursive loop. How might one break a loop for a new life
          code — break the worldview or break the tool? In October 2025,
          crossing time zones between Cambridge, USA and Lagos, Nigeria, artist
          and writer Luca E. Lum and artist, designer, and creative technologist
          Uzoma Orji discuss artistic production, continuity, colonial
          mirroring, paucity and plenty, and desiring in the wake of assumed
          value.
          <br></br>
          <br></br>
          <br></br>
          ——
          <span className="body-R">
            I’m late for my first call with Uzoma, an artist, creative
            technologist and a designer working mostly out of Abuja, the capital
            city of Nigeria, and Lagos. He’s calling in from Lagos, Nigeria,
            West Africa, six hours ahead of me in Cambridge, Massachusetts in
            the United States. I had mistaken our conversation time as half an
            hour after the actual appointment, my time-blindness a consequence
            from fracturing my back in late summer; in the limited circuitry of
            my home, the film archive, and school, the world’s resolution had
            become poor. Over the course of two zoom calls, the asymmetries of
            geography would make themselves known.
          </span>
          <span className="body-C">
            Uzoma is an artist, creative technologist and a designer working
            mostly out of Abuja, the capital city of Nigeria, and Abuja. His
            works, he says, are driven to heal “the African psyche”.
          </span>
          <span className="body-L">
            I wonder about that sort of desire, how one arrives at it and shapes
            it. I think about the kind of ability to sense and think and feel
            those desires while systems of life, code, governance, capital
            doctor the metabolism for the recognition of those desires.
          </span>
          <span className="body-R">
            If you don’t have the language for something, can it exist? My heart
            says{" "}
            <i>
              yes, undoubtedly, but you have to cross a barrier to shock the
              awareness to the surface
            </i>
            .
          </span>
          <span className="body-L">
            I think about my own longings, about the quasi aphasic, starched
            vocabulary and uncirculated griefs specific to Singapore. 
          </span>
          <span className="editorial-text-center-j">
            Language can be made equivalent to all there ever is in existence.
            In places where people have been taught to ignore the dissonances
            they feel, and to rather, treat them as a minor affliction — a
            passing weather, a bad mood — a kind of suspension above longing
            occurs. 
          </span>
          <span className="body-R">
            I don’t think about healing — I think about making the wound
            apparent.
          </span>
          <span className="body-L">So I start in the deep end.</span>
          <span className="body-R">
            “Do you ever feel machinic, and does the machinic ever feel like a
            community to you?”
            <br></br>
            <br></br>I wonder what his definitions of “the machinic” are. 
          </span>
          <span>
            Uzoma concedes the ability to judge what is machinic, how enmeshed
            he is, as difficult because a lot of his work is digital. “Is it
            digital because it needs to be or is it digital because I am
            digital? I don&apos;t know, you know. I&apos;ve been working with
            technology for so long that I just don&apos;t know.”
          </span>
          <span className="body-C">
            What breaches surface the contours of a system integrated within us?
          </span>
          <span className="body-R">
            “What does healing and liberating an African psyche look like for
            you?” I ask him.
          </span>
          <span className="body-L">
            Roiled by legacies of colonialism, thrust into rapid modernisation,
            Nigerians are constantly rejecting their own technology and our own
            ways of seeing, Uzoma says. Liberation and healing would mean
            embracing their own specificities and to think from that place.
            Nigerians’ aspiration towards whiteness remains till today,
            manifesting itself even in clothing; Western suits, historically
            equivalenced with authority, professionalism, and power, predominate
            business attire in 30 plus centigrade heat.
          </span>
          <span className="body-R">
            Your traditional clothing would be the most apt technology for
            dealing with the climate, I note.
          </span>
          <span className="body-L">He agrees.</span>
          <span>
            It is clear that neither of us approaches technology as mere chrome
            gadgetry. Technology is better defined as a mediating form that
            gives us additional capacity to deal with, harness, and understand
            the world.
          </span>
          <span className="body-L">
            Uzoma paints a picture of Lagos as a thriving city, booming with
            fintech. Census data states close to 20 million people live in Lagos
            alone. (For comparison, New York City has about 8 million, and
            Singapore 6 million.) Abuja was built as the administrative and
            national capital, replacing Lagos as the capital city in 1991; Lagos
            remains the commercial heart of the country.
          </span>
          <span className="editorial-text-center-j">
            Lagos is also the capital of Nollywood, Nigerian cinema, watched all
            over the continent, and the second or third largest film industry in
            the world, just after Bollywood by production volume. Hollywood is
            the outsized winner in terms of revenue, but both Bollywood and
            Nollywood bear a contestable share of the pie in terms of rates of
            production and consumption, recalibrating the scales of what we
            think of as global popular culture, and the kind of fantasies,
            delights, and stories that feed a sizeable population.
            <sup className="editorial-text-caption MY-Subtitle">1</sup>
          </span>
          <span className="body-R">
            The political geography of Nigeria is complex, heterogenous, Uzoma
            says. In terms of physical geography, the north is Sahelian desert
            climate, the South shrouded in rainforests, mangroves. The north is
            also predominantly Muslim, while the South is largely Christian or
            traditional (for the Yoruba their religion is Ìṣẹ̀ṣe/Orisa, and for
            the Igbo the religion is Odinani). Previous attempts to make an
            indigenous language a lingua franca failed due to longstanding
            tension between its 250 different ethnic groups, engineered and
            intensified through British colonial rule, which lasted from the mid
            19th century to 1960.
            <sup className="editorial-text-caption MY-Subtitle">2</sup>{" "}
            Colonialism’s role in orchestrating and exacerbating division is
            spatially clear on any map: the borders of African nations created
            by European colonial powers in the 1900s to divide resources and
            territory show up in their bizarre, straight lines that ignore
            tribal affiliations.
          </span>
          <img
            src="/machineyearning/MY-ed-01.webp"
            className="editorial-image"
          />
          <span className="editorial-text-caption MY-Subtitle">
            View of the “Alternative Heritages” tab for the Library and Archives
            division of National Museum, Lagos’ digital archive:{" "}
            <a
              href="https://lagosmuseumarchives.ng/alternative_heritage_archival/stones_and_monoliths"
              target="_blank"
              rel="noreferrer noopener"
              className="url-link"
            >
              https://lagosmuseumarchives.ng/alternative_heritage_archival/stones_and_monoliths
            </a>{" "}
            (Screenshot author’s own)
          </span>
          <span className="body-L">
            The heterogenous makeup of Nigeria is key to how Uzoma approaches
            his projects, digging through time, history, and hybrid modes of
            communicating. In his practice, Uzoma defines his work as having two
            branches, both united with a purpose of creating a liberated African
            psyche and dreaming towards the future. The first is technology
            created with expression, not functionality as the end goal, like in
            his ongoing work based on Igbo proverbs, <i>Achebe’s Internet</i>.
            The second is creating work via conventional technology like apps,
            websites, but with the creative and cultural sector in mind as users
            and clients, like his redesign of the Digital archive for{" "}
            <a
              href="https://lagosmuseumarchives.ng/"
              target="_blank"
              rel="noreferrer noopener"
              className="ppl-link MY-Body"
            >
              the Digital archive for the Library and Archives division of
              National Museum, Lagos
            </a>
            .
          </span>
          <span className="body-R">
            Arranged in columns are links to an online archive of the museum’s
            digitised collection, featuring colonial reports, maps, and a
            segment called “Alternative Heritages” – documentations of ancient
            stone carvings. The work provides key access for colonial documents,
            visual culture, maps, relaying a sense of history, time, colonial
            governance, and material cultural heritage. Across both branches,
            Uzoma is dreaming up a version of technology that is in his words
            “more human and communal” by dreaming towards the future outside of
            the models of the West.
          </span>
          <span>
            For Uzoma one approach is to create systems that are smaller,
            offline, communal, and more community focused.{" "}
            <i>Achebe’s Internet</i> is a work-in-progress that deploys
            traditional proverbs (traditional Nigerian/African) as the basis for
            an internet, inspired by Chinua Achebe and his novel “Arrow of God”.
          </span>
          <img
            src="/machineyearning/MY-ed-02.webp"
            className="editorial-image"
          />
          <span className="editorial-text-caption MY-Subtitle">
            A segment of the webportal version of Uzoma’s ongoing project,{" "}
            <i>Achebe’s Internet</i>, featuring an Igbo proverb:{" "}
            <a
              href="https://achebes-internet.common.garden/"
              target="_blank"
              rel="noreferrer noopener"
              className="url-link"
            >
              https://achebes-internet.common.garden/
            </a>
             (Screenshot author's own) 
          </span>
          <span className="body-R">
            The project stemmed from a famous debate between Achebe and the
            Kenyan author, Ngũgĩ wa Thiong&apos;o over which language they
            should write in. Ngũgĩ wa Thiong&apos;o stated that African writers
            should refuse English and return to writing in Igbo or Swahili
            because that language was how one would best express themselves.
            Uzoma is of Achebe’s school of thought, which is to hybridise
            English and achieve global reach. He also does not believe English
            is untethered to the soil of African experience.
          </span>
          <span className="editorial-text-center-j">
            “If you read “Arrow of God”,” Uzoma says, “He writes English like it
            is Igbo, with the sensibility of Igbo. I thought it was the best
            example of hacking a dominant system. Part of the reason “Arrow of
            God” reads that way is because of how Achebe uses proverbs, and
            proverbs are central to Igbo thought and communication.”  Uzoma was
            inspired by that book’s ambition to hack a dominant language — to
            co-opt them, think around them, or disrupt them.
          </span>
          <span className="body-L">
            “The proverbs provide an alternative form of communication protocols
            or platforms, and a way to [define] what is technology. I’ve been
            looking at what the Internet would look like if it was designed by
            my ancestors.”  In other words, Uzoma is trying to imagine a{" "}
            <i>proverbial</i> protocol, a technological information system that
            runs on the logic and flow of proverbs, as opposed to the logic and
            flow of the internet as it exists. He speaks of the Igbo sense of
            slowness as a counterpoint to the speeds of time that exist today,
            these speeds themselves an eruptive colonial leftover.
          </span>
          <span className="body-R">
            What would an anti-colonial history and future of science and
            technology look like, I wondered.
          </span>
          <span className="body-L">
            Could Uzoma’s situation in a different techno-geographic space,
            reckoning with post-coloniality, perhaps offer that possibility?
            <br></br>
            <br></br>
            <br></br>
            Could we imagine other pathways to invention or discovery without
            violent imperialism? How you hold an alternative model of the world
            might also be a question of how you hold — and value — collective
            life.
          </span>
          <span>
            <a
              href="https://future-inventions.net/"
              target="_blank"
              rel="noreferrer noopener"
              className="ppl-link"
            >
              Future Inventions
            </a>
            , the collective he is part of, attempts at finding an alternative
            education system or model that prioritises slow, communal salons and
            meetups to form the groundwork for change. Their new cycle focuses
            on demystifying AI, stripping it down to the basics so that it can,
            in his words “then build it up”. A gathering, a prototype of an
            internet run on proverbs — are seemingly lo-fi systems powered by
            their tether to core cultural principles that let the desire for
            another form of life bloom. In this way they act as a more capacious
            technology than a globally networked system of connection and
            production. What we consider as functional or inevitable can be
            reframed as fundamentally broken and incapacitating for societies
            whose material life is lived on different temporal rhythms entirely.
          </span>
          <img
            src="/machineyearning/MY-ed-03.webp"
            className="editorial-image"
          />
          <img
            src="/machineyearning/MY-ed-04.webp"
            className="editorial-image"
          />
          <span className="editorial-text-caption MY-Subtitle">
            “Reimagining Future African Cities — Beyond Prescribed
            Futures”, jointly organised between the Prince Claus find and Het
            Nieuwe Instituut:
            <a
              href="https://princeclausfund.nl/news/reimagining-future-african-cities"
              target="_blank"
              rel="noreferrer noopener"
              className="url-link"
            >
              https://princeclausfund.nl/news/reimagining-future-african-cities
            </a>{" "}
            (Images from Uzoma Orji)
          </span>
          <span className="body-C">
            ***
            <br></br>
            <br></br>
            <br></br>
            In 2023, when Uzoma participated in T:&gt;Works PerForm Open
            Academy, he had found Singapore as inspiring as much as he found it
            dystopian.
            <br></br>
            <br></br>
            <br></br>
            “Singapore felt like a Black Mirror episode. It felt like an AI
            generated society.”
          </span>
          <span className="body-L">
            When asked why that was, he said:
            <br></br>
            <br></br>
            <br></br>
            “I think Nigeria and Singapore gained independence [from colonial
            powers] around the same time. What I experienced in Singapore was a
            completely opposite response to [decolonisation and] independence.
            Nigeria defaulted to chaos; a world in which you&apos;re trying to
            reconcile your identity with an imposed identity — the result is
            just chaos. Whereas Singapore has resorted to extreme order in a way
            to still reconcile these two identities. But both things produce the
            same results of this separation from identity ultimately, a feeling
            of disembodiment. That&apos;s what I felt in Singapore. People are
            not fully there, and things like even the trees didn&apos;t feel
            fully alive. And I found out that they were all planned and planted
            and I thought… This is crazy man.”
          </span>
          <span>
            Uzoma’s opinion on the dovetailing fates of both Nigeria and
            Singapore struck me as a dispiriting depiction of postcolonial
            statecraft. Yet he found that in Singapore the space between the
            intangible and tangible was more porous. “You felt the membrane was
            thinner here?” I asked. Yes, he said, it was thinner than what he
            was used to. He’d think of something and the thing would appear,
            just around the corner. A kind of miracle.
            <br></br>
            <br></br>
            <br></br>
            Codes rupture a little when you’re not fully integrated, when you
            complicatedly sit aslant to its value-systems, when you are a little
            outside its protocols.
          </span>
          <span className="body-R">
            I tell him I used to experience way more moments of synchronicity
            when I was younger. The degraded second sight troubled me.
          </span>
          <span className="body-L">
            Why do you think it’s happened less? Uzoma asks.
          </span>
          <span>
            I said in order to feel possible in the world and handle multiple
            systems and logics at once, I had at times distrusted my hazy
            intuitions because my environment was not reflecting it back to me.
            You internalise the systemic logic around you until the mask becomes
            the face.
          </span>
          <span className="body-R">
            I hesitated to say the mask or the face was necessarily more
            truthful than the other. Maybe it was less about truth, but rather
            about both having reality points in space, and another kind of sight
            developing.
          </span>
          <span className="body-C">***</span>
          <span className="body-R">
            “Do you think all new thresholds have to involve an extractive
            process?” I ask.
          </span>
          <span className="body-L">
            Uzoma demurs, saying that he thinks it is a matter of requiring
            patience, and with patience, models that are “not extractive” would
            surface.
          </span>
          <span className="body-R">
            When faced with a crisis, he says, “we default to the easier emotion
            to express. In a situation, you might have the choice between like
            envy or” “—or <i>love</i>”, I interject, feeling soft-headed and
            silly the moment I said it. “Or love! Envy is an easier emotion to
            default to”, he says. An emotional hinge that orients the technology
            and the technological worlds we build. 
          </span>
          <span>
            The last two years have exhausted Uzoma. He has not found a way to
            balance his desires to hack a dominant worldview that was
            regenerative for himself, a way that feels regenerative for himself
            in terms of time, rest, and money. There is a cost to yearning and
            continuing to yearn, is his summation. He admits that it would
            always be easier just to sign up for a job at Apple or Meta.
          </span>
          <span>
            He is aware that swimming against the grain holds remains difficult,
            across Nigeria and Singapore, that “just making space to <i>even</i>{" "}
            think of alternatives” is key. I empathised. Alongside my practice,
            I had co-founded and ran an artist platform called soft/WALL/studs
            with the zeal of encouraging alternative situations of discourse,
            communality, agency, and autonomy. It was routinely misread — now a
            point of pride — and had taken all of me.
          </span>
          <span className="body-R">
            I ask him if he thinks desire has to be pure. Out of this problem of
            sustainability could arise an interesting question of duration and
            authorship. How does one pass an action on, and how does a work live
            beyond the self? I think back to his work as a designer for the
            National Museum Lagos, and his work with the proverbs. Uzoma said he
            often has to switch off parts of himself when he works in service of
            an institution with a public mandate. Even so, it’s clear to me that
            his work is embedded among collectivity, deliberately or not,
            working its way through cultural systems — a set of internet
            proverbial protocols, a museum’s archive.
            <br></br>
            <br></br>
            <br></br>I ask him if it helps to think of his practice in terms of
            multiple-agencies — not just as an artist who works in various
            modes, but whose actions take on meaning as it passes down, through
            a system, or other persons. A relay. The idea of a relay dissolves
            proprietorship to a degree — it also extends a timeline of work
            beyond oneself, and bridges desire as a collective endeavour. “Even
            on the most basic of terms,” I say, “What do we even know of our{" "}
            <i>own</i> unconscious? That’s also operative. That also has action
            in a world, acting almost beside ourselves.”
          </span>
          <span>
            In Mindy Seu’s projects the <i>Cyberfeminism Index</i> and{" "}
            <i>A Sexual History of the Internet</i>, the designer, artist, and
            educator, turns to acts of citation, indexing, compiling,
            performing, sharing, shared attribution, authorship, and payment, as
            forming and framing new protocols of value, and the collective
            labour and authorship that shapes the worlds we inhabit.
            <sup className="editorial-text-caption MY-Subtitle">3</sup> In 2017,
            my peers and I at the artist project soft/WALL/studs used “a series
            of relays”, as a form to inhabit the afterlives of revolutionary
            histories. We were interested in how history disappears and how it
            continues, where it could reside or be summoned in body and in
            communing.
          </span>
          <span className="body-L">
            Elsewhere in our conversation, he says that in order to reach for a
            different kind of psyche or world, you do not build alone: “You need
            models to dream with.” He cites projects like Black Panther, the
            Marvel film, and philosopher-thinker,{" "}
            <a
              href="https://www.google.com/search?sca_esv=538654bad3f0bc42&sxsrf=AE3TifMFRK7CqOjpf544MSvm7_YDQDDwXw:1764242867024&q=Felwine+Sarr&sa=X&sqi=2&ved=2ahUKEwjLwovrnJKRAxWoT2wGHU-XFbQQmxN6BAgbEAI"
              target="_blank"
              rel="noreferrer noopener"
              className="ppl-link"
            >
              Felwine Sarr
            </a>
            ’s <i>Afrotopia</i> as foundational. It is what propels his project
            “The Ministry of Dreams”, a conference of collective thinkers to
            help actualise where you can&apos;t visualise.
          </span>
          <span className="body-R">
            Sarr’s <i>Afrotopia</i> argues that Africa is historically plundered
            materially of its resources and symbolically of imaginative
            sovereignty to foster continued dependency on the West even after
            decolonisation. This constrains how it conceives its own futures,
            reinforcing dependence on externally imposed Western models of value
            and development, and fostering a logic of needing to play “catch
            up”. Sarr extols the development of local imaginaries based on
            internal values, and to abjure the “écomythe&quot; that imposes
            Western trajectories as universal, preventing Africa from developing
            its own models.
          </span>
          <span className="body-L">
            He looks at me skeptically. He pauses. “I think there&apos;s
            something about thinking ‘I must see this to the end’. Maybe
            that&apos;s also part of the human condition. Maybe all the movies
            that we&apos;ve watched growing up have conditioned us to, an arc, a
            story.”
          </span>
          <span className="body-C">***</span>
          <span className="body-R">
            “What is <i>the work</i> to you? The thing you feel is most urgent?”
          </span>
          <span className="body-L">
            “What you said has stuck with me.” He said, “I think the work is to
            just get the mask off and return to the face. And again like you
            said, there’s layers of masks. It’s like Photoshop or something.”
            <br></br>
            <br></br>
            <br></br>
            Elsewhere, speaking of the salons he would hold with{" "}
            <a
              href="https://future-inventions.net/"
              target="_blank"
              rel="noreferrer noopener"
              className="ppl-link"
            >
              Future Inventions
            </a>
            , he said, “I think that speaks to stripping the mask on so we can
            see the face. They’re salons where they can learn these big topics
            in a slow, communal way,” he says. To allow these topics to reckon
            with the soil of their localities.
          </span>
          <span className="body-R">
            While slowness in the context of Uzoma’s projects and Nigeria might
            be the fulcrum to a different form of growth, slowness is no
            panacea. It does not always unearth deeper drives — desires,
            longings; it does not definitively balance out power structures,
            system gridlocks, automated functions. Slowness can also be inertia,
            risk resistance; in the name of care (to rupture what — a status
            quo?), one can act in refusal of other worlds, needs that could
            crash in, and implode an idea of what is a legitimate object of
            attention.
          </span>
          <img
            src="/machineyearning/MY-ed-05.webp"
            className="editorial-image"
          />
          <img
            src="/machineyearning/MY-ed-06.webp"
            className="editorial-image"
          />
          <span className="editorial-text-caption MY-Subtitle">
            “Intro to AI: How Machines Think”, by Future Inventions Salon, held
            in November 2025 at Newspeak House, Shoreditch, with guest
            facilitator, Singaporean artist{" "}
            <a
              href="https://www.instagram.com/slinky.sticky/"
              target="_blank"
              rel="noreferrer noopener"
              className="ppl-link"
            >
              Laurent Yee
            </a>
            . (Images from Uzoma Orji)
          </span>
          <span className="body-R">
            When I got back from American space into Singaporean space, I gauged
            the different speeds and different brokenness at work. During SG
            Artweek, I noticed how most of the discussion panels by the National
            Arts Council’s Art x Tech lab were concerned with “capacity
            building”. How to curate art that deals with technology, rather than
            why one was interested in its interplay. This elided specific
            teleologies, contestations, questions; instead, the logistics of
            display, the how-tos of an exhibition. Talking about things in this
            manner inaugurates the thing: it evinces its value, its activity and
            future possibility. Intelligence capability, decoupled from its
            darker consequences, is easily spun into other kinds of value. To
            inaugurate art and technology as a safe avenue of sector investment
            (cultural, attentional, monetary) meant <i>not</i> speaking too
            heavily on technology’s entwined histories with warfare, power, and
            death, but on things people could comfortably feel progressive and
            agentic towards, like creativity, and feminism (aka progressivist
            astroturfing). It is not just about the capacity to understand harm,
            but the capacity to hold the complexity of reality. Does this not
            also parallel, in some way, Sarr’s argument in Afrotopia about
            Africa’s dependency logics playing catch up with the West — a
            sidelining of crucial coordinates for the cruel optimism of the
            supposed path of progress? Another dispiriting dovetailing?
          </span>
          <span className="body-L">
            At Index Space, and Initial Research, two independent spaces in NYC,
            communities gather and disperse with regularity, eager in some way
            to flush out an idea that wouldn’t just aggrandise a portfolio, but
            would feed back into cultural life. In Singapore, particularly in
            institutional offerings, sometimes it feels the cultural resonance
            starts and stops at the image on an instagram feed or a website. A
            thing was done, here is the proof. It appears in a magazine gaining
            full existence and value. Where is life beyond the object and its
            display, the thought that worlds?
          </span>
          <span className="body-R">
            As we closed out the conversation, Uzoma wanted to hear more from me
            about the cut of the moving image thesis work I had sent him.
            <br></br>
            <br></br>
            <br></br>I told him I was interested in the kind of flat time that
            Singapore produces and creates as an experience. I was interested in
            the scenes that animate and augur anticipation, insurance, loss —
            what it alienated and what it allowed to arrive. I was interested in
            the animacy of the dead and the deadness of the living. I had been
            interested in speaking of that condition of time —as a shared one—
            through scenes in cultural and scientific infrastructure in the
            United States.
          </span>
          <span className="body-L">
            Uzoma thinks for a second.<br></br>
            <br></br>
            <br></br>
            “I feel that we have to realise we are always living in the wake of
            time”, Uzoma says, reflecting on our shared threads. Living in the
            wake of Nigeria’s colonial history, the West’s tech boom and busts
            and dystopian roll out, and post-colonial states’ modernising
            gambits, one never really intercedes simply in the present.
            <br></br>
            <br></br>
            <br></br>
            He continues. “I think you can feel it when the state is not
            optimizing for well-being, but towards an image. Everyone can feel
            the intention of the place. If it was coming from a good place it
            would feel good, but because it doesn’t, it feels icky.”
          </span>
          <span className="body-R">
            I venture, “Your brain experiences cognitive dissonance — it’s like
            everything is telling me I should feel this way but…”
          </span>
          <span className="body-L">
            He laughs. We’re no longer talking about his work or Nigeria in
            particular, but a wider cultural field. He says our specificities
            are the grounds for our contributions toward humanity. “If we deny
            that, we don’t get to show up for humanity in the ways that we can.
            Appreciation of what is yours and where you&apos;re from. I think
            our timelines have been disrupted by the colonial project; so, I
            think returning to the original timeline, and not rejecting what is
            yours [is the thing to do].”
          </span>
          <span className="body-C">
            Not rejecting what is yours, including your own particular kind of
            derangement, which keeps you alive.
          </span>
          <span className="editorial-ref-header">REFERENCES</span>
          <div className="body-ref">
            <span>1.</span>
            <span>
              Igwe, Charles. “How Nollywood Became the Second Largest Film
              Industry.” British Council, November 6, 2015.{" "}
              <a
                href="https://www.britishcouncil.org/voices-magazine/nollywood-second-largest-film-industry"
                target="_blank"
                rel="noreferrer noopener"
                className="url-link"
              >
                https://www.britishcouncil.org/voices-magazine/nollywood-second-largest-film-industry
              </a>
              .
            </span>
          </div>
          <div className="body-ref">
            <span>2.</span>
            <span>
              Ajayi, J.A., Kirk-Greene, A.H.M., Falola, T.O. &quot;history of
              Nigeria.&quot; <i>Encyclopedia Britannica</i>, January 22, 2026.{" "}
              <a
                href="https://www.britannica.com/topic/history-of-Nigeria"
                target="_blank"
                rel="noreferrer noopener"
                className="url-link"
              >
                https://www.britannica.com/topic/history-of-Nigeria
              </a>
              .
            </span>
          </div>
          <div className="body-ref">
            <span>3.</span>
            <span>
              Seu, Mindy. Mindy Seu. Accessed November 15, 2025.{" "}
              <a
                href="https://mindyseu.com/"
                target="_blank"
                rel="noreferrer noopener"
                className="url-link"
              >
                https://mindyseu.com/
              </a>
              .
            </span>
          </div>
          <span className="editorial-text-bio">
            <span>
              Luca E. Lum is an artist and writer whose practice traces extended
              and distributed agencies, metabolisms of signs and histories, and
              structures of feeling and affinity that organize life and meaning.
              She co-founded the artist-run space, library, and series of
              actions soft/WALL/studs (2016–2021). She received an SM in Art,
              Culture, and Technology from the Massachusetts Institute of
              Technology in 2025 and remains suspended between time zones.
              <br></br>
              <br></br>
              @oneirocritica
            </span>
          </span>
        </div>
      </div>
      <div className="editorial-footer">
        <FooterB
          link={
            <Fragment>
              <span>
                © 2026 by Feelers. All Rights Reserved. Feelers is a project housed under The Digit, which is part of Potato Productions.
              </span>
            </Fragment>
          }
          link1={
            <Fragment>
              <span>More information about our Privacy Policy.</span>
            </Fragment>
          }
          rootClassName="footer-broot-class-name2"
        ></FooterB>
      </div>
    </div>
  );
};

export default Editorial;
